import React from 'react';
import Box, { BoxProps } from '@mui/material/Box';
import { tannerGray } from '../ThemeProvider/colors';
import { Search as SearchIcon } from '@octanner/prism-icons';
import { AutocompleteRenderInputParams, styled } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '../Paper';
import { Close } from '@octanner/prism-icons';
import TextField from '../TextField';
import {
  tannerSemantic,
  tannerBlack,
  tannerWhite,
} from '../ThemeProvider/colors';

const getColor = ({
  sub,
  disabled,
  light,
}: {
  sub?: boolean;
  disabled?: boolean;
  light?: boolean;
}) => {
  if (disabled) return tannerSemantic.info[600];
  return sub || light ? tannerGray[600] : tannerBlack;
};

export const TitleContainer = styled('div')({
  alignItems: 'flex-start',
  display: 'flex',
  flex: '1 1 auto',
  flexDirection: 'column',
  justifyContent: 'center',
  paddingLeft: 8,
});

export const Title = styled('div', { name: 'Title' })<{
  sub?: boolean;
  light?: boolean;
  disabled?: boolean;
}>(({ sub, light, disabled }) => ({
  color: getColor({ sub, disabled, light }),
  fontSize: sub ? 12 : 14,
  alignSelf: light ? 'center' : undefined,
  borderRadius: '4px',
}));

export const AvatarTitleWrap = styled('div', { name: 'AvatarTitleWrap' })<{
  disabled?: boolean;
}>(({ disabled }) => ({
  display: 'flex',
  flex: '1 1 auto',
  opacity: disabled ? 0.5 : 1,
}));

export const CheckWrap = styled('div', { name: 'CheckWrap' })({
  width: 24,
  textAlign: 'right',
});

const StyledSpan = styled('span')({
  marginRight: '8px',
  marginLeft: '8px',
  fontSize: 16,
  display: 'flex',
  alignItems: 'center',
});

interface StyledBoxProps extends BoxProps {
  disabled?: boolean;
  check?: boolean;
}
interface CloseIconProps {
  size?: string;
}

export const StyledBox = styled(Box)<StyledBoxProps>(({ disabled, check }) => ({
  borderRadius: 3,
  display: 'flex',
  padding: 8,
  width: '100%',
  pointerEvents: disabled ? 'none' : 'auto',
  '&&.MuiAutocomplete-option': {
    backgroundColor: check ? tannerSemantic.info[100] : tannerWhite,
    marginTop: '4px',
  },
  '&&.MuiAutocomplete-option.Mui-focused': {
    backgroundColor: check ? tannerSemantic.info[100] : tannerGray['50'],
  },
}));

export const StyledCloseIcon = styled(Close)<CloseIconProps>(({ size }) => ({
  color: size && tannerGray['600'],
}));

export const TextFieldContainer = styled(TextField)({
  '&& .MuiOutlinedInput-root': {
    paddingLeft: '40px',
    '&.MuiInputBase-sizeSmall': {
      paddingLeft: '40px',
    },
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: tannerGray['600'],
  },
  '& .search-icon': {
    color: tannerGray['500'],
    position: 'absolute',
    left: 14,
    top: '50%',
    transform: 'translateY(-50%)',
  },
  '& .MuiOutlinedInput-root:hover .search-icon': {
    color: tannerGray['600'],
  },
});

export const StyledCircularProgress = styled(CircularProgress)({
  position: 'absolute',
  left: 16,
  top: 'calc(50% - 8px)',
  transform: 'translateY(-50%)',
});

export const renderStartAdornment = (
  loading?: boolean,
  isSingleSearch?: boolean,
  defaultAdornment?: React.ReactNode,
) => {
  const LoaderComponent = isSingleSearch
    ? CircularProgress
    : StyledCircularProgress;

  const adornmentIcon = loading ? (
    <LoaderComponent size="16px" aria-label="loading" />
  ) : (
    <SearchIcon className={!isSingleSearch ? 'search-icon' : undefined} />
  );

  return (
    <>
      {isSingleSearch ? (
        <StyledSpan>{adornmentIcon}</StyledSpan>
      ) : (
        adornmentIcon
      )}
      {defaultAdornment}
    </>
  );
};

export const getSearchInputStyles = (
  disabled: boolean | undefined,
  props: AutocompleteRenderInputParams,
  loading: boolean | undefined,
  isSingleSearch: boolean | undefined = false,
) => ({
  sx: () =>
    disabled
      ? {
          backgroundColor: tannerGray['100'],
          color: tannerGray['400'],
        }
      : {
          ...(props.size === 'small' && {
            backgroundColor: tannerGray['50'],
          }),
        },
  ...props.InputProps,
  startAdornment: (
    <>
      {renderStartAdornment(
        loading,
        isSingleSearch,
        props.InputProps.startAdornment,
      )}
    </>
  ),
});

export const CustomPaper = ({ ...paperProps }) => (
  <Paper
    sx={{
      mt: '4px',
      '& .MuiAutocomplete-noOptions': {
        color: tannerGray['600'],
        textAlign: 'center',
        padding: '24px',
        fontSize: '14px',
      },
    }}
    {...paperProps}
  />
);
